import { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { Button, Space, Dropdown } from 'antd';
import { ZoomInOutlined, ZoomOutOutlined, FullscreenOutlined, DownloadOutlined } from '@ant-design/icons';
import { downloadChart } from '../../../utils/chartDownload';

const OrgChartRenderer = ({ data, config = {} }) => {
  const svgRef = useRef();
  const containerRef = useRef();
  const [dimensions, setDimensions] = useState({ width: 0, height: 600 });

  useEffect(() => {
    if (containerRef.current) {
      const width = config.width || containerRef.current.offsetWidth || 800;
      const height = config.height || 600;
      setDimensions({ width, height });
    }
  }, [config]);

  useEffect(() => {
    if (!data || !dimensions.width) return;

    renderChart();
  }, [data, dimensions]);

  const renderChart = () => {
    // Clear previous
    d3.select(svgRef.current).selectAll('*').remove();

    const margin = { top: 60, right: 120, bottom: 60, left: 120 };
    const width = dimensions.width - margin.left - margin.right;
    const height = dimensions.height - margin.top - margin.bottom;

    const svg = d3.select(svgRef.current)
      .attr('width', dimensions.width)
      .attr('height', dimensions.height);

    // Add zoom behavior
    const g = svg.append('g');

    const zoom = d3.zoom()
      .scaleExtent([0.3, 2])
      .on('zoom', (event) => {
        g.attr('transform', event.transform);
      });

    svg.call(zoom);

    // Create hierarchy
    const root = d3.hierarchy(data);
    
    // Calculate better tree size based on number of nodes
    const nodeCount = root.descendants().length;
    const treeWidth = Math.max(width, nodeCount * 150);
    const treeHeight = Math.max(height, root.height * 180);
    
    const treeLayout = d3.tree()
      .size([treeWidth, treeHeight])
      .separation((a, b) => {
        // More spacing between nodes
        return (a.parent === b.parent ? 1.5 : 2);
      });

    treeLayout(root);
    
    // Center the tree
    const xOffset = (dimensions.width - treeWidth) / 2 + margin.left;
    const yOffset = margin.top;
    g.attr('transform', `translate(${xOffset},${yOffset})`);

    // Add gradient definitions
    const defs = svg.append('defs');

    // Gradient for nodes
    const gradient = defs.append('linearGradient')
      .attr('id', 'nodeGradient')
      .attr('x1', '0%')
      .attr('y1', '0%')
      .attr('x2', '100%')
      .attr('y2', '100%');

    gradient.append('stop')
      .attr('offset', '0%')
      .attr('stop-color', '#667eea');

    gradient.append('stop')
      .attr('offset', '100%')
      .attr('stop-color', '#764ba2');

    // Shadow filter
    const filter = defs.append('filter')
      .attr('id', 'shadow')
      .attr('x', '-50%')
      .attr('y', '-50%')
      .attr('width', '200%')
      .attr('height', '200%');

    filter.append('feGaussianBlur')
      .attr('in', 'SourceAlpha')
      .attr('stdDeviation', 3);

    filter.append('feOffset')
      .attr('dx', 0)
      .attr('dy', 2)
      .attr('result', 'offsetblur');

    filter.append('feComponentTransfer')
      .append('feFuncA')
      .attr('type', 'linear')
      .attr('slope', 0.3);

    const feMerge = filter.append('feMerge');
    feMerge.append('feMergeNode');
    feMerge.append('feMergeNode').attr('in', 'SourceGraphic');

    // Add links (curved lines)
    const link = g.selectAll('.link')
      .data(root.links())
      .enter()
      .append('path')
      .attr('class', 'link')
      .attr('d', d3.linkVertical()
        .x(d => d.x)
        .y(d => d.y))
      .attr('fill', 'none')
      .attr('stroke', '#667eea')
      .attr('stroke-width', 2)
      .attr('stroke-opacity', 0.6)
      .attr('opacity', 0);

    // Animate links
    link.transition()
      .duration(800)
      .delay((d, i) => i * 50)
      .attr('opacity', 1);

    // Add nodes
    const node = g.selectAll('.node')
      .data(root.descendants())
      .enter()
      .append('g')
      .attr('class', 'node')
      .attr('transform', d => `translate(${d.x},${d.y})`)
      .style('opacity', 0)
      .style('cursor', 'pointer');

    // Node background
    node.append('rect')
      .attr('x', -110)
      .attr('y', -35)
      .attr('width', 220)
      .attr('height', 70)
      .attr('rx', 10)
      .attr('ry', 10)
      .attr('fill', 'url(#nodeGradient)')
      .attr('filter', 'url(#shadow)')
      .attr('stroke', 'rgba(255,255,255,0.3)')
      .attr('stroke-width', 2);

    // Node title (wrap text if too long)
    node.each(function(d) {
      const nodeGroup = d3.select(this);
      const title = d.data.title || d.data.name;
      const name = d.data.name !== d.data.title ? d.data.name : '';
      
      // Add title
      nodeGroup.append('text')
        .attr('dy', name ? '-0.3em' : '0.3em')
        .attr('text-anchor', 'middle')
        .attr('fill', 'white')
        .attr('font-size', '13px')
        .attr('font-weight', 'bold')
        .text(title.length > 25 ? title.substring(0, 22) + '...' : title);
      
      // Add name if different from title
      if (name) {
        nodeGroup.append('text')
          .attr('dy', '1.2em')
          .attr('text-anchor', 'middle')
          .attr('fill', 'rgba(255,255,255,0.85)')
          .attr('font-size', '11px')
          .text(name.length > 25 ? name.substring(0, 22) + '...' : name);
      }
    });

    // Animate nodes
    node.transition()
      .duration(800)
      .delay((d, i) => i * 50)
      .style('opacity', 1);

    // Hover effects
    node.on('mouseenter', function() {
      d3.select(this).select('rect')
        .transition()
        .duration(200)
        .attr('transform', 'scale(1.05)');
    });

    node.on('mouseleave', function() {
      d3.select(this).select('rect')
        .transition()
        .duration(200)
        .attr('transform', 'scale(1)');
    });
    
    // Auto-fit the chart after rendering
    setTimeout(() => {
      const bounds = g.node().getBBox();
      const fullWidth = dimensions.width;
      const fullHeight = dimensions.height;
      const width = bounds.width;
      const height = bounds.height;
      const midX = bounds.x + width / 2;
      const midY = bounds.y + height / 2;
      
      const scale = 0.75 / Math.max(width / fullWidth, height / fullHeight);
      const translate = [fullWidth / 2 - scale * midX, fullHeight / 2 - scale * midY];
      
      svg.transition()
        .duration(750)
        .call(
          zoom.transform,
          d3.zoomIdentity.translate(translate[0], translate[1]).scale(scale)
        );
    }, 100);
  };

  const handleZoomIn = () => {
    d3.select(svgRef.current)
      .transition()
      .call(d3.zoom().scaleBy, 1.3);
  };

  const handleZoomOut = () => {
    d3.select(svgRef.current)
      .transition()
      .call(d3.zoom().scaleBy, 0.7);
  };

  const handleReset = () => {
    const svg = d3.select(svgRef.current);
    const bounds = svg.select('g').node().getBBox();
    const fullWidth = dimensions.width;
    const fullHeight = dimensions.height;
    const width = bounds.width;
    const height = bounds.height;
    const midX = bounds.x + width / 2;
    const midY = bounds.y + height / 2;
    
    const scale = 0.8 / Math.max(width / fullWidth, height / fullHeight);
    const translate = [fullWidth / 2 - scale * midX, fullHeight / 2 - scale * midY];
    
    svg.transition()
      .duration(750)
      .call(
        d3.zoom().transform,
        d3.zoomIdentity.translate(translate[0], translate[1]).scale(scale)
      );
  };

  const handleDownload = (format) => {
    if (svgRef.current) {
      downloadChart(svgRef.current, 'org-chart', format);
    }
  };

  const downloadMenuItems = [
    {
      key: 'png',
      label: 'Download as PNG',
      onClick: () => handleDownload('png'),
    },
    {
      key: 'jpg',
      label: 'Download as JPG',
      onClick: () => handleDownload('jpg'),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
      <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
        <Space style={{ marginBottom: '12px' }}>
          <Button icon={<ZoomInOutlined />} onClick={handleZoomIn} size="small">
            Zoom In
          </Button>
          <Button icon={<ZoomOutOutlined />} onClick={handleZoomOut} size="small">
            Zoom Out
          </Button>
          <Button icon={<FullscreenOutlined />} onClick={handleReset} size="small">
            Reset
          </Button>
        </Space>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <svg ref={svgRef} style={{ border: '1px solid #f0f0f0', borderRadius: '4px', maxWidth: '100%', height: 'auto' }} />
        </div>
      </div>
    </div>
  );
};

export default OrgChartRenderer;
